import { type PlainFormSectionChild, useComputed } from "@homebound/beam";
import type { FieldState } from "@homebound/form-state";
import {
  cutoffTag,
  type OptionTags,
} from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { OptionVariantRows } from "src/routes/projects/change-events/guided/workflows/components/OptionVariantRows";
import type { OptionVariantRows_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/components/OptionVariantRows.generated";
import type { PersonalizationOptionCard_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/components/PersonalizationOptionCard.generated";

export type PersonalizationOptionCardProps = {
  /** The group with the single option that turns the personalization on, e.g. "Add Washer and Dryer". */
  group: PersonalizationOptionCard_CheckoutConfigOptionGroupFragment;
  /** The group's `childGroup`. Its options are the variants the user picks from once the personalization is on. */
  childGroup: OptionVariantRows_CheckoutConfigOptionGroupFragment;
  /** The form field that stores whether the personalization is on. */
  selectedField: FieldState<boolean | null | undefined>;
  /** The form field that stores the selected variant's `ReadyPlanOption` id. Only matters while `selectedField` is on. */
  variantField: FieldState<string | null | undefined>;
  priceMode?: OptionPriceMode;
  /**
   * Tags keyed by `ReadyPlanOption` id. The group's own option's takes the section's tag slot over the cutoff, and the
   * variants' sit next to their names.
   */
  optionTags?: OptionTags;
};

/**
 * Builds the `FormSection` child for an optional personalization, e.g. adding a washer and dryer. It shows a checkbox
 * that turns the personalization on, followed by one radio row per variant, which stay disabled until it is on.
 */
export function personalizationOptionCard(props: PersonalizationOptionCardProps): PlainFormSectionChild {
  const { group, childGroup, selectedField, variantField, priceMode, optionTags } = props;
  // The group only has one option, and that option is what's being added, so its name makes a better title.
  const option = group.options.first;
  return {
    id: group.id,
    title: option?.option.name ?? group.group.name,
    description: group.group.marketingDescription ?? undefined,
    tag: (option && optionTags?.[option.option.id]) ?? cutoffTag(group.cutoffDate),
    selection: { mode: "toggle", field: selectedField },
    fields: (
      <PersonalizationVariants
        childGroup={childGroup}
        selectedField={selectedField}
        variantField={variantField}
        priceMode={priceMode}
        optionTags={optionTags}
      />
    ),
  };
}

type PersonalizationVariantsProps = Pick<
  PersonalizationOptionCardProps,
  "childGroup" | "selectedField" | "variantField" | "priceMode" | "optionTags"
>;

/** The variant rows, which stay disabled until the personalization is turned on. */
function PersonalizationVariants(props: PersonalizationVariantsProps) {
  const { childGroup, selectedField, variantField, priceMode, optionTags } = props;
  const isOn = useComputed(() => !!selectedField.value, [selectedField]);
  return (
    <OptionVariantRows
      group={childGroup}
      field={variantField}
      disabled={!isOn}
      priceMode={priceMode}
      optionTags={optionTags}
    />
  );
}
